class FigureDatabaseController {
  constructor(apiService) {
    this.apiService = apiService;
  }

  /**
   * Create a new figure database entry
   * @param {string} figureId - Figure ID to create entry for
   * @returns {Promise<Object>}
   */
  async createEntry(figureId) {
    try {
      const response = await this.apiService.post('/figure-database', { figureId });
      return response.data;
    } catch (error) {
      console.error('Error creating figure database entry:', error);
      throw error;
    }
  }

  /**
   * Get latest figure database entries
   * @param {Object} query - Query parameters (limit, offset, etc.)
   * @returns {Promise<Object>}
   */
  async getLatestEntries(query = {}) {
    try {
      const response = await this.apiService.get('/figure-database', { params: query });
      return response.data;
    } catch (error) {
      console.error('Error fetching figure database entries:', error);
      throw error;
    }
  }

  /**
   * Get distinct encyclopedia facet values (paginated)
   * @param {string} type - facet type (origin, character, company, artist, classification, material)
   * @param {Object} query - Query parameters (q, limit, offset)
   * @returns {Promise<{data: string[], count: number, limit: number, offset: number}>}
   */
  async getEncyclopediaFacetValues(type, query = {}) {
    try {
      const safeType = encodeURIComponent(String(type || ''));
      const response = await this.apiService.get(`/figure-database/encyclopedia/facets/${safeType}`, { params: query });
      return response;
    } catch (error) {
      console.error('Error fetching encyclopedia facet values:', error);
      throw error;
    }
  }

  /**
   * Get trending figure database entries
   * @param {Object} params - Query parameters (limit, offset)
   * @returns {Promise<Object>}
   */
  async getTrendingFigures(params = {}) {
    try {
      const response = await this.apiService.get('/figure-database/trending', { params });

      console.log("Response: ", response)
      return response;
    } catch (error) {
      console.error('Error fetching trending figures:', error);
      throw error;
    }
  }

  /**
   * Get a specific figure database entry
   * @param {string} id - Figure database entry ID
   * @returns {Promise<Object>}
   */
  async getEntry(id) {
    try {
      const response = await this.apiService.get(`/figure-database/${id}`);
      return {
        statusCode: response.statusCode,
        data: response.data?.data || response.data,
      };
    } catch (error) {
      console.error(`Error fetching figure database entry ${id}:`, error);
      throw error;
    }
  }

  /**
   * Update a figure database entry
   * @param {string} id - Figure database entry ID
   * @param {Object} updateDto - Updated data
   * @returns {Promise<Object>}
   */
  async updateEntry(id, updateDto) {
    try {
      const response = await this.apiService.put(`/figure-database/${id}`, updateDto);
      return response.data;
    } catch (error) {
      console.error(`Error updating figure database entry ${id}:`, error);
      throw error;
    }
  }

  /**
   * Get figure database settings
   * @returns {Promise<Object>}
   */
  async getSettings() {
    try {
      const response = await this.apiService.get('/figure-database/settings');
      return response.data;
    } catch (error) {
      console.error('Error fetching figure database settings:', error);
      throw error;
    }
  }

  async getAdminStats() {
    try {
      const response = await this.apiService.get('/figure-database/admin/stats');
      return response.data;
    } catch (error) {
      console.error('Error fetching figure database stats:', error);
      throw error;
    }
  }

  async deleteEntry(id) {
    try {
      const response = await this.apiService.delete(`/figure-database/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting figure database entry:', error);
      throw error;
    }
  }

  /**
   * Get latest releases by release date
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>}
   */
  async getLatestReleasesByReleaseDate(params = {}) {
    try {
      const response = await this.apiService.get('/figure-database/latest-releases/by-release-date', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching latest releases by release date:', error);
      throw error;
    }
  }

  /**
   * Get latest releases by added date
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>}
   */
  async getLatestReleasesByAddedDate(params = {}) {
    try {
      const response = await this.apiService.get('/figure-database/latest-releases/by-added-date', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching latest releases by added date:', error);
      throw error;
    }
  }

  /**
   * Get latest releases combined
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>}
   */
  async getLatestReleasesCombined(params = {}) {
    try {
      const response = await this.apiService.get('/figure-database/latest-releases/combined', { params });

      return {
        success: true,
        statusCode: response.status,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching latest releases combined:', error);
      throw error;
    }
  }

  /**
   * Create a comment on a figure
   * @param {Object} commentData - Comment data
   * @returns {Promise<Object>}
   */
  async createComment(commentData) {
    try {
      const { figureId, ...payload } = commentData;
      const response = await this.apiService.post(`/figure-database/${figureId}/comments`, payload);

      return {
        statusCode: response.statusCode,
        data: response.data?.data || response.data,
      };
    } catch (error) {
      console.error('Error creating comment:', error);
      throw error;
    }
  }

  /**
   * Get comments for a figure
   * @param {Object} query - Query parameters
   * @returns {Promise<Object>}
   */
  async getComments(query) {
    try {
      const { figureId, ...params } = query;
      const response = await this.apiService.get(`/figure-database/${figureId}/comments`, { params });
      return {
        statusCode: response.statusCode,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching comments:', error);
      throw error;
    }
  }

  /**
   * Update a comment
   * @param {string} commentId - Comment ID
   * @param {Object} updateData - Update data
   * @returns {Promise<Object>}
   */
  async updateComment(commentId, updateData) {
    try {
      const response = await this.apiService.put(`/figure-database/comments/${commentId}`, updateData);
      return response.data;
    } catch (error) {
      console.error('Error updating comment:', error);
      throw error;
    }
  }

  /**
   * Delete a comment
   * @param {string} commentId - Comment ID
   * @returns {Promise<Object>}
   */
  async deleteComment(commentId) {
    try {
      const response = await this.apiService.delete(`/figure-database/comments/${commentId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting comment:', error);
      throw error;
    }
  }

  /**
   * Add emoji reaction to a comment
   * @param {string} commentId - Comment ID
   * @param {string} emoji - Emoji to add
   * @returns {Promise<Object>}
   */
  async addEmojiReaction(commentId, emoji) {
    try {
      const response = await this.apiService.post(`/figure-database/comments/${commentId}/reactions`, { emoji });
      return response.data;
    } catch (error) {
      console.error('Error adding emoji reaction:', error);
      throw error;
    }
  }

  /**
   * Remove emoji reaction from a comment
   * @param {string} commentId - Comment ID
   * @param {string} emoji - Emoji to remove
   * @returns {Promise<Object>}
   */
  async removeEmojiReaction(commentId, emoji) {
    try {
      const response = await this.apiService.delete(`/figure-database/comments/${commentId}/reactions`, {
        params: { emoji }
      });
      return response.data;
    } catch (error) {
      console.error('Error removing emoji reaction:', error);
      throw error;
    }
  }

  /**
   * Add figure database entry to shelf
   * @param {Object} shelfData - Shelf data
   * @returns {Promise<Object>}
   */
  async addToShelf(shelfData) {
    try {
      const response = await this.apiService.post('/figure-database/shelf', shelfData);
      return {
        statusCode: response.status,
        data: response.data?.data || response.data,
      };
    } catch (error) {
      console.error('Error adding to shelf:', error);
      throw error;
    }
  }

  /**
   * Remove figure database entry from shelf
   * @param {string} figureDatabaseId - Figure database entry ID
   * @returns {Promise<Object>}
   */
  async removeFromShelf(figureDatabaseId) {
    try {
      const response = await this.apiService.delete(`/figure-database/shelf/${figureDatabaseId}`);
      return response.data;
    } catch (error) {
      console.error('Error removing from shelf:', error);
      throw error;
    }
  }

  /**
   * Check if figure database entry is in shelf
   * @param {string} figureDatabaseId - Figure database entry ID
   * @returns {Promise<Object>}
   */
  async checkInShelf(figureDatabaseId) {
    try {
      const response = await this.apiService.get(`/figure-database/shelf/check/${figureDatabaseId}`);
      return {
        statusCode: response.status,
        data: response.data?.data || response.data,
      };
    } catch (error) {
      console.error('Error checking shelf status:', error);
      throw error;
    }
  }

  /**
   * Get user's figure database shelf
   * @param {string} userId - User ID
   * @param {Object} query - Query parameters
   * @returns {Promise<Object>}
   */
  async getUserShelf(userId, query = {}) {
    try {
      const response = await this.apiService.get(`/figure-database/shelf/user/${userId}`, { params: query });
      return response.data;
    } catch (error) {
      console.error('Error fetching user shelf:', error);
      throw error;
    }
  }

  /**
   * Upload user image for figure database entry
   * @param {Object} imageData - Image data
   * @returns {Promise<Object>}
   */
  async uploadUserImage(imageData) {
    try {
      const response = await this.apiService.post('/figure-database/user-images', imageData);
      return response.data;
    } catch (error) {
      console.error('Error uploading user image:', error);
      throw error;
    }
  }

  /**
   * Get user images for figure database entry
   * @param {Object} query - Query parameters
   * @returns {Promise<Object>}
   */
  async getUserImages(query) {
    try {
      const { figureDatabaseId, ...params } = query;
      const response = await this.apiService.get(`/figure-database/${figureDatabaseId}/user-images`, { params });
      return {
        statusCode: response.status,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching user images:', error);
      throw error;
    }
  }

  /**
   * Delete user image
   * @param {string} imageId - Image ID
   * @returns {Promise<Object>}
   */
  async deleteUserImage(imageId) {
    try {
      const response = await this.apiService.delete(`/figure-database/user-images/${imageId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting user image:', error);
      throw error;
    }
  }

  /**
   * Get figure database drafts
   * @param {Object} params - Query parameters (status, createdById, companyId, page, limit)
   * @returns {Promise<Object>}
   */
  async getFigureDatabaseDrafts(params = {}) {
    try {
      const response = await this.apiService.get('/figure-database-drafts', { params });
      return {
        statusCode: response.status,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching figure database drafts:', error);
      throw error;
    }
  }

  /**
   * Get a specific figure database draft by ID
   * @param {string} id - Draft ID
   * @returns {Promise<Object>}
   */
  async getFigureDatabaseDraftById(id) {
    try {
      const response = await this.apiService.get(`/figure-database-drafts/${id}`);
      return {
        statusCode: response.status,
        data: response.data,
      };
    } catch (error) {
      console.error(`Error fetching figure database draft ${id}:`, error);
      throw error;
    }
  }

  /**
   * Approve a figure database draft
   * @param {string} id - Draft ID
   * @returns {Promise<Object>}
   */
  async approveFigureDatabaseDraft(id) {
    try {
      const response = await this.apiService.post(`/figure-database-drafts/${id}/approve`);
      return {
        statusCode: response.status,
        data: response.data,
      };
    } catch (error) {
      console.error(`Error approving figure database draft ${id}:`, error);
      throw error;
    }
  }

  /**
   * Reject a figure database draft
   * @param {string} id - Draft ID
   * @param {string} reason - Rejection reason
   * @returns {Promise<Object>}
   */
  async rejectFigureDatabaseDraft(id, reason) {
    try {
      const response = await this.apiService.post(`/figure-database-drafts/${id}/reject`, { reason });
      return {
        statusCode: response.status,
        data: response.data,
      };
    } catch (error) {
      console.error(`Error rejecting figure database draft ${id}:`, error);
      throw error;
    }
  }

  /**
   * Create a figure database draft
   * @param {Object} draftData - Draft data
   * @returns {Promise<Object>}
   */
  async createFigureDatabaseDraft(draftData) {
    try {
      const response = await this.apiService.post('/figure-database-drafts', draftData);
      return {
        statusCode: response.status,
        data: response.data,
      };
    } catch (error) {
      console.error('Error creating figure database draft:', error);
      throw error;
    }
  }

  /**
   * Update a figure database draft
   * @param {string} id - Draft ID
   * @param {Object} updateData - Update data
   * @returns {Promise<Object>}
   */
  async updateFigureDatabaseDraft(id, updateData) {
    try {
      const response = await this.apiService.patch(`/figure-database-drafts/${id}`, updateData);
      return {
        statusCode: response.status,
        data: response.data,
      };
    } catch (error) {
      console.error(`Error updating figure database draft ${id}:`, error);
      throw error;
    }
  }

  /**
   * Delete a figure database draft
   * @param {string} id - Draft ID
   * @returns {Promise<Object>}
   */
  async deleteFigureDatabaseDraft(id) {
    try {
      const response = await this.apiService.delete(`/figure-database-drafts/${id}`);
      return {
        statusCode: response.status,
        data: response.data,
      };
    } catch (error) {
      console.error(`Error deleting figure database draft ${id}:`, error);
      throw error;
    }
  }
}

export default FigureDatabaseController;
